import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import AppShell from '../components/AppShell';
import Button from '../components/Button';
import Field from '../components/Field';
import { VERTICAL_GROUPS, VERTICALS } from '../data/verticals';
import { useAuth } from '../lib/useAuth';
import { cx } from '../lib/cx';

interface Hero {
  images: { url: string }[];
  verticals: { id: string; label: string; group: string; blurb: string }[];
}

/**
 * The hero is a wall of real building photographs with one question over it
 * (CLAUDE.md §10) — not a headline on a gradient. The point is to show the
 * product in the first second: this is a thing about looking at buildings.
 *
 * It drifts slowly, once, and then stops moving unless someone clicks. If the
 * library is empty, or the visitor prefers reduced motion, it is a flat grid,
 * which is a perfectly good hero on its own.
 */
function PhotoWall({ images }: { images: { url: string }[] }) {
  if (images.length === 0) {
    return <div className="absolute inset-0 bg-ink" aria-hidden="true" />;
  }

  // Three columns that drift at different rates reads as depth rather than as
  // a carousel. Duplicated so the loop has no seam.
  const columns = [0, 1, 2].map((c) => images.filter((_, i) => i % 3 === c));

  return (
    <div className="absolute inset-0 overflow-hidden bg-ink" aria-hidden="true">
      <div className="flex h-full gap-2 opacity-45">
        {columns.map((col, ci) => (
          <div
            key={ci}
            className={cx(
              'flex flex-1 flex-col gap-2',
              ci === 1 ? 'animate-drift-slow' : ci === 2 ? 'animate-drift-slower' : 'animate-drift',
            )}
          >
            {[...col, ...col].map((img, i) => (
              <img
                key={`${ci}-${i}`}
                src={img.url}
                alt=""
                loading={i < 2 ? 'eager' : 'lazy'}
                className="w-full flex-shrink-0 object-cover"
                style={{ aspectRatio: '4 / 3' }}
              />
            ))}
          </div>
        ))}
      </div>
      {/* Enough scrim that the question over the top always reads. */}
      <div className="absolute inset-0 bg-gradient-to-b from-ink/85 via-ink/70 to-ink/90" />
    </div>
  );
}

export default function Landing() {
  const { user } = useAuth();
  const [hero, setHero] = useState<Hero | null>(null);
  const [query, setQuery] = useState('');

  useEffect(() => {
    let cancelled = false;
    fetch('/api/public/hero')
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => !cancelled && d && setHero(d as Hero))
      .catch(() => {
        /* The wall is decoration; the page works without it. */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // The building list is bundled, so the picker survives the API being down or
  // slow. The endpoint is still the source of truth once it answers — it knows
  // which verticals actually have a photo library behind them.
  const matches = useMemo(() => {
    const list =
      hero?.verticals ??
      VERTICALS.map((v) => ({ id: v.id, label: v.label, group: v.group, blurb: v.blurb }));
    const q = query.trim().toLowerCase();
    if (!q) return list;
    return list.filter((v) => `${v.label} ${v.blurb} ${v.group}`.toLowerCase().includes(q));
  }, [hero, query]);

  return (
    <AppShell
      action={
        user ? (
          <Link to="/dashboard" className="t-meta min-h-tap inline-flex items-center font-semibold text-green-700">
            My projects
          </Link>
        ) : (
          <Link to="/login" className="t-meta min-h-tap inline-flex items-center font-semibold text-green-700">
            Sign in
          </Link>
        )
      }
    >
      {/* Hero. Full-bleed out of the shell's padding. */}
      <section className="relative -mx-gutter -mt-12 flex min-h-[26rem] items-center px-gutter py-20 sm:-mt-16 sm:min-h-[34rem]">
        <PhotoWall images={hero?.images ?? []} />
        <div className="relative mx-auto w-full max-w-wrap">
          <h1 className="t-display max-w-[15ch] text-white">What are you building?</h1>
          <p className="t-body-lg measure mt-6 text-grey-300">
            Answer a few questions, pick the buildings that catch your eye, and get concept
            images and a written design direction you can hand to a design professional.
          </p>
          <div className="mt-9 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
            <Link to={user ? '/new' : '/request-access'} className="sm:w-auto">
              <Button block>{user ? 'Start a project' : 'Request access'}</Button>
            </Link>
            {!user && (
              <Link to="/login" className="sm:w-auto">
                <Button variant="secondary" block>
                  I already have access
                </Button>
              </Link>
            )}
          </div>
        </div>
      </section>

      {/* The picker, immediately below the fold. */}
      <section className="mt-16">
        <h2 className="t-h2 text-ink">Pick the closest thing to yours</h2>
        <p className="t-body measure mt-2 text-grey-600">
          Twenty-seven building types, each with its own questions and its own way of
          working out how big it needs to be.
        </p>

        <div className="mt-6 max-w-md">
          <Field
            id="landing-search"
            label="Search building types"
            type="search"
            placeholder="Church, warehouse, clinic…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>

        {VERTICAL_GROUPS.map((group) => {
          const items = matches.filter((v) => v.group === group);
          if (!items.length) return null;
          return (
            <div key={group} className="mt-10">
              <h3 className="t-h3 text-ink">{group}</h3>
              <ul className="mt-4 flex flex-wrap gap-2">
                {items.map((v) => (
                  <li key={v.id}>
                    <Link
                      to={user ? '/new' : '/request-access'}
                      className="t-meta min-h-tap inline-flex items-center rounded-full border border-grey-300 bg-white px-5 text-ink transition-colors hover:border-green-600 hover:bg-green-050"
                    >
                      {v.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}

        {matches.length === 0 && query.trim() && (
          <p className="t-body mt-8 text-grey-600">
            Nothing matched that. Try a broader word, or{' '}
            <Link to="/request-access" className="font-semibold text-green-700 underline decoration-1 underline-offset-4">
              ask us
            </Link>
            .
          </p>
        )}
      </section>

      {/* How it works. Four steps, because that is how many there are. */}
      <section className="mt-20 border-t border-grey-200 pt-12">
        <h2 className="t-h2 text-ink">How it works</h2>
        <ol className="mt-8 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
          {[
            ['Answer a few questions', 'About your building, in plain language. A rough answer is fine.'],
            ['See how big it needs to be', 'Square footage, parking, land, and a rough budget range.'],
            ['Pick the buildings you like', 'Five to ten photographs out of about fifty. Go on instinct.'],
            ['Get concepts and a brief', 'Images to argue with, and a written direction to hand to a design professional.'],
          ].map(([title, body], i) => (
            <li key={title}>
              <p className="t-numeral text-2xl text-green-600">{i + 1}</p>
              <p className="t-h3 mt-2 text-ink">{title}</p>
              <p className="t-meta measure mt-2 text-grey-600">{body}</p>
            </li>
          ))}
        </ol>
      </section>

      <section className="mt-20 rounded-lg bg-ink px-7 py-12 sm:px-10">
        <p className="t-eyebrow text-green-400">Etrenzik Design</p>
        <p className="t-h2 mt-3 max-w-[24ch] text-white">
          A starting point, not a finished design.
        </p>
        <p className="t-body measure mt-4 text-grey-300">
          This tool exists to get you from an idea to something you can put on a table —
          concepts, a size, a rough budget, and a written direction — before you sit down
          with a design professional.
        </p>
        <div className="mt-8">
          <Link to={user ? '/new' : '/request-access'}>
            <Button>{user ? 'Start a project' : 'Request access'}</Button>
          </Link>
        </div>
      </section>
    </AppShell>
  );
}
